import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth, api } from '../../context/AuthContext';

const MFASetup = ({ onSuccess, onCancel }) => {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);

  const { token } = useAuth();

  useEffect(() => {
    initMFASetup();
  }, []);

  const initMFASetup = async () => {
    try {
      const response = await api.post('/auth/mfa/setup', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setQrCode(response.data.qr_code_url);
      setSecret(response.data.secret);
    } catch (err) {
      setError('Fehler beim Initialisieren des MFA-Setups');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setError('');
    setEnabling(true);

    try {
      await api.post(
        '/auth/mfa/enable',
        null,
        {
          params: { mfa_code: mfaCode },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      onSuccess();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Ungültiger MFA-Code. Bitte versuchen Sie es erneut.'
      );
      setMfaCode('');
    } finally {
      setEnabling(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">MFA-Setup wird vorbereitet...</p>
      </div>
    );
  }

  return (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h4 className="mb-4">Zwei-Faktor-Authentifizierung einrichten</h4>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="mb-4">
          <p className="text-muted">
            Scannen Sie diesen QR-Code mit Ihrer Authenticator-App
            (z.B. Google Authenticator, Authy)
          </p>

          {qrCode && (
            <div className="text-center my-4">
              <img src={qrCode} alt="MFA QR Code" className="img-fluid" style={{ maxWidth: '250px' }} />
            </div>
          )}

          <Alert variant="info">
            <strong>Backup-Code:</strong>
            <div className="mt-2">
              <code className="fs-6">{secret}</code>
            </div>
            <small className="d-block mt-2">
              Bewahren Sie diesen Code sicher auf. Sie können ihn verwenden, um Ihr Konto wiederherzustellen.
            </small>
          </Alert>
        </div>

        <Form onSubmit={handleEnable}>
          <Form.Group className="mb-4">
            <Form.Label>Bestätigungs-Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="000000"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              maxLength={6}
              className="text-center fs-4"
            />
            <Form.Text className="text-muted">
              Geben Sie den 6-stelligen Code aus Ihrer App ein, um die Einrichtung abzuschließen
            </Form.Text>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              variant="primary"
              type="submit"
              className="btn-pcs flex-grow-1"
              disabled={enabling || mfaCode.length !== 6}
            >
              {enabling ? 'Aktivierung läuft...' : 'MFA aktivieren'}
            </Button>
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={enabling}
            >
              Abbrechen
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default MFASetup;
